import { ButtonLink } from '@/components/Atoms/ButtonLink/ButtonLink';
import { Stack } from '@/components/Molecules/Stack/Stack';
import { useDebouncedEffect } from '@/hooks/useDebouncedEffect';
import useTranslation from '@/hooks/useTranslation';
import MagnifyIcon from '@bloomberg/web-icons-react/Magnify';
import type { ChangeEvent, InputHTMLAttributes } from 'react';
import { useRef, useState } from 'react';
import type { FCl } from 'react-fc';

/**
 * A simple input designed for search.
 *
 * For performance purposes SearchInput is debounced by 300ms by default, meaning `onChangeValue` will be delayed.
 * You can override the debounce value to something more suitable or 0 for no debounce.
 */
export const SearchInput: FCl<IProps> = ({
    onChangeValue,
    suggestExactMatch = true,
    suggestExactMatchAutoSubmit = true,
    debounce = 300,
    value,
    ...rest
}) => {
    const [currentValue, setCurrentValue] = useState<string>(value?.toString());
    const inputRef = useRef<HTMLInputElement>(null);
    const submitButtonRef = useRef<HTMLButtonElement>(null);
    const hasQuotes = currentValue?.includes('"');
    const hasMultipleWords = currentValue?.trim().includes(' ');
    useDebouncedEffect(
        () => {
            if (onChangeValue) {
                onChangeValue(currentValue);
            }
        },
        debounce,
        [currentValue]
    );

    return (
        <Stack>
            <div className="cds-input-group">
                <input
                    maxLength={200}
                    autoComplete="off"
                    type="search"
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        // convert curly quotes to straight quotes so that "exact string" works
                        setCurrentValue(event.target.value.replace(/[\u201C\u201D]/g, '"'));
                    }}
                    value={currentValue}
                    ref={inputRef}
                    {...rest}
                />
                <button
                    className="cds-button cds-button--tertiary cds-button--icon"
                    type="submit"
                    aria-label={rest.placeholder}
                    ref={submitButtonRef}
                    form={rest.form}
                >
                    <MagnifyIcon />
                </button>
            </div>
            {suggestExactMatch && !hasQuotes && hasMultipleWords && (
                <SearchInputSuggestion
                    performExactMatch={(v) => {
                        setCurrentValue(v);
                        if (suggestExactMatchAutoSubmit && inputRef.current?.form && submitButtonRef.current) {
                            // React's setCurrentValue may not have time to reflect the changes yet
                            inputRef.current.value = v;
                            // form.submit() doesn't trigger onSubmit event, clicking the submit button instead
                            submitButtonRef.current.click();
                        }
                    }}
                    value={currentValue}
                />
            )}
        </Stack>
    );
};

interface ISearchInputSuggestionProps {
    performExactMatch: (val: string) => void;
    value: string;
}

const SearchInputSuggestion = ({ performExactMatch, value }: ISearchInputSuggestionProps) => {
    const { t } = useTranslation('common');
    const exactMatchValue = `"${value}"`;
    return (
        <div>
            <ButtonLink onClick={() => performExactMatch(exactMatchValue)}>
                {t('Actions.try_exact_match')} <span>{exactMatchValue}</span>
            </ButtonLink>
        </div>
    );
};

interface IProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
    /** Callback to use when characters are typed. Will be passed a string of the value */
    onChangeValue?: (val: string) => void;
    suggestExactMatch?: boolean;
    suggestExactMatchAutoSubmit?: boolean;
    /** The amount of time before onChangeValue should fire */
    debounce?: number;
    placeholder?: string;
}
